// Shared navigation, workspace context and accessible landmarks. With a workspace the whole navigation is
// the sidebar (the sections as text, the language, the account); on a narrow screen it is a header with
// the logo and a «Menú» button that opens it. Without a workspace (sign-in, sign-up, create organisation),
// the logo sits above the form title and the language sits beside the form — the same shell for visitors
// and for a signed-in person who still has no organisation.
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  ExitIcon,
  HamburgerMenuIcon,
  LockClosedIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  Button as AriaButton,
  ListBox,
  ListBoxItem,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Select,
  SelectValue,
  Separator,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { type Me, meQuery } from './api';
import { authClient } from './auth-client';
import { LANGUAGE_NAMES, LOCALES, type Locale } from './i18n';
import { Logo } from './logo';

/** The languages of the app, as a drop-down list. */
function LanguageSelect({
  onChange,
  className = 'language-select',
}: {
  onChange: (locale: Locale) => void;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Select
      aria-label={t('app.language')}
      selectedKey={i18n.language}
      onSelectionChange={(key) => onChange(key as Locale)}
      className={className}
    >
      <AriaButton className="language-trigger">
        <SelectValue />
        <ChevronDownIcon aria-hidden="true" />
      </AriaButton>
      <Popover className="menu-popover">
        <ListBox className="menu-list">
          {LOCALES.map((code) => (
            <ListBoxItem key={code} id={code} textValue={LANGUAGE_NAMES[code]} className="menu-item">
              <span lang={code}>{LANGUAGE_NAMES[code]}</span>
              <CheckIcon className="menu-item-check" aria-hidden="true" />
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}

/**
 * Who is signed in, at the foot of the sidebar. Its menu is the person's own: their profile, their security
 * and the end of the session (the settings of each website are in the website, not here).
 */
function AccountMenu({
  me,
  onGo,
  onSignOut,
}: {
  me: Me;
  onGo: (path: string) => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();
  return (
    <MenuTrigger>
      <AriaButton className="sidebar-account">
        <span className="account-monogram" aria-hidden="true">
          {me.user.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="account-text">
          <strong className="block wrap-anywhere">{me.user.name}</strong>
          <span className="account-meta">{t(`home.roles.${me.organization?.role}`)}</span>
          <span className="account-meta">
            {t('account.planLabel', { plan: t(`users.plans.${me.plan}`) })}
          </span>
        </span>
        <ChevronUpIcon className="account-chevron" aria-hidden="true" />
      </AriaButton>
      <Popover placement="top" className="menu-popover">
        <Menu
          aria-label={t('app.userMenu')}
          onAction={(key) => (key === 'sign-out' ? onSignOut() : onGo(String(key)))}
          className="menu-list"
        >
          <MenuItem id="/account/profile" textValue={t('account.menuProfile')} className="menu-item">
            <PersonIcon aria-hidden="true" />
            {t('account.menuProfile')}
          </MenuItem>
          <MenuItem id="/account/security" textValue={t('account.menuSecurity')} className="menu-item">
            <LockClosedIcon aria-hidden="true" />
            {t('account.menuSecurity')}
          </MenuItem>
          <Separator className="menu-separator" />
          <MenuItem id="sign-out" textValue={t('app.signOut')} className="menu-item">
            <ExitIcon aria-hidden="true" />
            {t('app.signOut')}
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

export function RootLayout() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: me } = useQuery(meQuery);
  const workspace = Boolean(me?.organization);

  useEffect(() => {
    if (me && me.user.locale !== i18n.language) void i18n.changeLanguage(me.user.locale);
  }, [me, i18n]);

  async function switchLanguage(locale: Locale) {
    if (locale === i18n.language) return;
    await i18n.changeLanguage(locale);
    if (me) {
      await authClient.updateUser({ locale });
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    }
  }

  async function signOut() {
    await authClient.signOut();
    queryClient.setQueryData(meQuery.queryKey, null);
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== meQuery.queryKey[0] });
    await navigate({ to: '/login' });
  }

  // On a narrow screen the sidebar is a menu that opens from a button: it closes when a section is chosen
  // or the page changes, and with Escape, which gives the focus back to the button.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: closing the menu is the reaction to a new page.
  useEffect(() => setMenuOpen(false), [pathname]);
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={workspace ? 'app-shell' : 'public-shell'}>
      <a href="#contenido" className="skip-link">
        {t('app.skip')}
      </a>
      {workspace && (
        // biome-ignore lint/a11y/noStaticElementInteractions: Escape only closes the menu; every control inside is a real one.
        <aside
          className="app-sidebar"
          data-open={menuOpen || undefined}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || !menuOpen) return;
            // A list that is open (the language, the account) takes the Escape first.
            if ((event.target as Element).closest('[role="listbox"], [role="menu"]')) return;
            if (event.defaultPrevented) return;
            setMenuOpen(false);
            menuButton.current?.focus();
          }}
        >
          <div className="sidebar-top">
            <Link to="/" aria-label={t('app.home')} className="brand-link">
              <Logo />
            </Link>
            <button
              ref={menuButton}
              type="button"
              className="menu-toggle"
              aria-expanded={menuOpen}
              aria-controls="menu-principal"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <Cross2Icon aria-hidden="true" /> : <HamburgerMenuIcon aria-hidden="true" />}
              {t('nav.menu')}
            </button>
          </div>
          <div id="menu-principal" className="sidebar-panel">
            <div className="workspace-label">
              <span className="workspace-avatar" aria-hidden="true">
                {me?.organization?.name.slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0">
                <strong className="block wrap-anywhere">{me?.organization?.name}</strong>
              </div>
            </div>
            <nav aria-label={t('nav.label')} className="app-nav">
              <Link
                to="/"
                activeOptions={{ exact: true }}
                aria-current={pathname === '/' || pathname.startsWith('/sites/') ? 'page' : undefined}
                onClick={closeMenu}
              >
                {t('nav.sites')}
              </Link>
              <Link
                to="/organization"
                aria-current={
                  pathname === '/organization' || pathname.startsWith('/organization/') ? 'page' : undefined
                }
                onClick={closeMenu}
              >
                {t('nav.organization')}
              </Link>
              <Link to="/integrations" onClick={closeMenu}>
                {t('nav.integrations')}
              </Link>
              {me?.superadmin && (
                <Link to="/users" onClick={closeMenu}>
                  {t('nav.users')}
                </Link>
              )}
            </nav>
            <div className="sidebar-language">
              {/* Same control as the public site: language name and chevron, no box. */}
              <LanguageSelect onChange={switchLanguage} className="language-select language-select-web" />
            </div>
            {me && <AccountMenu me={me} onGo={(path) => void navigate({ href: path })} onSignOut={signOut} />}
          </div>
        </aside>
      )}
      <div className="app-body">
        <div className={workspace ? '' : 'auth-layout auth-layout-bare'}>
          {/* No sidebar: language top-right, same look as the public site menu (name + chevron). */}
          {!workspace && (
            <div className="auth-lang">
              <LanguageSelect onChange={switchLanguage} className="language-select language-select-web" />
            </div>
          )}
          {!workspace && (
            <aside className="auth-story">
              <span className="eyebrow">{t('shell.product')}</span>
              <h2>{t('shell.headline')}</h2>
              <p>{t('shell.intro')}</p>
              <ol className="auth-steps">
                {['connect', 'understand', 'improve'].map((step, index) => (
                  <li key={step}>
                    <span aria-hidden="true">0{index + 1}</span>
                    <div>
                      <strong>{t(`shell.${step}`)}</strong>
                      <p>{t(`shell.${step}Hint`)}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="auth-note">{t('shell.note')}</p>
            </aside>
          )}
          <main id="contenido" tabIndex={-1} className="min-w-0 focus:outline-none">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
